import React, { useEffect } from "react";
import { DataGrid, GridColDef, GridCellParams, GridValueFormatterParams, GridValueGetterParams, GridSortModel, GridRowData, deDE, LocalizationV4 } from "@material-ui/data-grid";
import {
    Container,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    IconButton,
    Grid,
    CircularProgress
} from "@material-ui/core";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { KeyboardArrowRight } from "@material-ui/icons";
import { useStyles } from "./datenplausibilitaetStyles";
import { useHistory } from 'react-router';
import { useMsal } from "@azure/msal-react";
import { globalStyles } from "../../assets/globalStyles";
import { putMesswertGeflaggt, selectAktuellerWert, setAktuellerWert, selectGeflaggteMesswerteListe, selectListLoading, selectSorting, setSorting } from "./datenplausibilitaetSlice";
import { messwertLogGeflaggt, messwertLogGeflaggtStatus } from "../../utils/apiTypes";
import { getWithAuth } from '../../utils/api';
import { messstelle, recorder, messgroesse } from './../../utils/apiTypes';
import { getMessstellenForLookup, getMessstellenLookup, selectMessstellenDictionary } from '../../features/messstellenLookup/messstellenLookupSlice';
import { logError, nanosecondsTimeStampToDate, nanosecondsTimeStampToLocalStringWithoutSlice, nanosecondsTimeStampToUTCStringWithoutSlice } from './../../utils/utils';
import { addTabNotResetDate, onDropDownSelect, updateField } from '../netzwirtschaft/netzwirtschaftSlice';
import { selectDate } from "../../features/konfiguration/konfigurationSlice";
import { isAuthorized, rollen } from "../../utils/auth/authConfig";
import { errorHandler } from "../../utils/errorHandling";


export const DatenplausibilitaetGrid: React.FC = () => {
    const dispatch = useAppDispatch();
    const { instance } = useMsal();


    useEffect(() => {
        dispatch(getMessstellenForLookup());
    }, [instance]);

    const classes = {
        ...useStyles(),
        ...globalStyles(),
    };

    const rows: messwertLogGeflaggt[] = useAppSelector(selectGeflaggteMesswerteListe);
    const listLoading: boolean = useAppSelector(selectListLoading);
    const sorting: GridSortModel = useAppSelector(selectSorting);
    const messstellenDict = useAppSelector(selectMessstellenDictionary);
    const aktuellerWert = useAppSelector(selectAktuellerWert);

    const [visibleDialog, setVisibleDialog] = React.useState(false);
    const [dialogKommentar, setDialogKommentarInputValue] = React.useState(aktuellerWert.kommentar);
    const [dialogTitle, setDialogTitel] = React.useState("");
    const [dialogText, setDialogText] = React.useState("");
    const [dialogStatusCode, setDialogStatusCode] = React.useState(aktuellerWert.status);

    const isUTC = useAppSelector(selectDate);

    const history = useHistory();
    const linkToDetails = (uw: string, start: number, ende: number, messstellenName: string, regel: string) => {
        getWithAuth("MESSSTELLEID", {}, messstellenName).then((messstelle: messstelle) => {
            dispatch(addTabNotResetDate(messstelle.name));
            dispatch(
                onDropDownSelect({
                    field: "uw",
                    value: messstelle.umspannwerk
                })
            );
            dispatch(
                onDropDownSelect({
                    field: "messpunkt",
                    value: messstellenName
                })
            );
            const rekorder: recorder = {
                klasse: "c10m",
                hersteller: "Eberle",
                beschreibung: regel.startsWith("Langzeitflicker") ? "2 Stunden" : "10 Minuten",
                kategorie: "Spannungen"
            }
            dispatch(
                onDropDownSelect({
                    field: "rekorder",
                    value: { klasse: rekorder.klasse, beschreibung: rekorder.beschreibung, hersteller: rekorder.hersteller, kategorie: rekorder.kategorie, messstelle: messstellenName }
                })
            );
            const messgroessen: messgroesse[] = [];
            if (regel.includes("u1 ") || regel.endsWith("u1")) {
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u1",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
            }
            if (regel.includes("u2 ") || regel.endsWith("u2")) {
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u2",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
            }
            if (regel.includes("u3 ") || regel.endsWith("u3")) {
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u3",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
            }
            if (regel.includes("u12 ") || regel.endsWith("u12")) {
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u12",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
            }
            if (regel.includes("u23 ") || regel.endsWith("u23")) {
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u23",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
            }
            if (regel.includes("u31 ") || regel.endsWith("u31")) {
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u31",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
            }
            if (regel.includes("Harmonische")) {
                messgroessen.push({
                    beschreibung: "Harmonische 5. Ordnung",
                    feldname: "u12h5",
                    einheit: "%",
                    unterkategorie: "Harmonische u12"
                });
                messgroessen.push({
                    beschreibung: "Harmonische 5. Ordnung",
                    feldname: "u23h5",
                    einheit: "%",
                    unterkategorie: "Harmonische u23"
                });
                messgroessen.push({
                    beschreibung: "Harmonische 5. Ordnung",
                    feldname: "u31h5",
                    einheit: "%",
                    unterkategorie: "Harmonische u31"
                });
            }
            if (regel.includes("Spannung 1 Stunde lang oberhalb 110%")) {
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u12",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u32",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u31",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
            }
            if (regel.includes("Langzeitflicker")) {
                messgroessen.push({
                    beschreibung: "Langzeitflicker PLT",
                    feldname: "upltu1_plt",
                    einheit: "",
                    unterkategorie: "Spannungsstatistikgrößen"
                });
                messgroessen.push({
                    beschreibung: "Langzeitflicker PLT",
                    feldname: "upltu2_plt",
                    einheit: "",
                    unterkategorie: "Spannungsstatistikgrößen"
                });
                messgroessen.push({
                    beschreibung: "Langzeitflicker PLT",
                    feldname: "upltu3_plt",
                    einheit: "",
                    unterkategorie: "Spannungsstatistikgrößen"
                });
                messgroessen.push({
                    beschreibung: "Langzeitflicker PLT",
                    feldname: "upltu12_plt",
                    einheit: "",
                    unterkategorie: "Spannungsstatistikgrößen"
                });
                messgroessen.push({
                    beschreibung: "Langzeitflicker PLT",
                    feldname: "upltu23_plt",
                    einheit: "",
                    unterkategorie: "Spannungsstatistikgrößen"
                });
                messgroessen.push({
                    beschreibung: "Langzeitflicker PLT",
                    feldname: "upltu31_plt",
                    einheit: "",
                    unterkategorie: "Spannungsstatistikgrößen"
                });
            }
            if (regel.includes("Kurzzeitflicker")) {
                messgroessen.push({
                    beschreibung: "Kurzzeitflicker",
                    feldname: "ustat1_pst",
                    einheit: "",
                    unterkategorie: "Spannungsstatistikgrößen"
                });
                messgroessen.push({
                    beschreibung: "Kurzzeitflicker",
                    feldname: "ustat2_pst",
                    einheit: "",
                    unterkategorie: "Spannungsstatistikgrößen"
                });
                messgroessen.push({
                    beschreibung: "Kurzzeitflicker",
                    feldname: "ustat3_pst",
                    einheit: "",
                    unterkategorie: "Spannungsstatistikgrößen"
                });
                messgroessen.push({
                    beschreibung: "Kurzzeitflicker",
                    feldname: "ustat12_pst",
                    einheit: "",
                    unterkategorie: "Spannungsstatistikgrößen"
                });
                messgroessen.push({
                    beschreibung: "Kurzzeitflicker",
                    feldname: "ustat23_pst",
                    einheit: "",
                    unterkategorie: "Spannungsstatistikgrößen"
                });
                messgroessen.push({
                    beschreibung: "Kurzzeitflicker",
                    feldname: "ustat31_pst",
                    einheit: "",
                    unterkategorie: "Spannungsstatistikgrößen"
                });
            }
            if (regel.includes("Unsymmetrie")) {
                messgroessen.push({
                    beschreibung: "Unsymmetrie u 2 (Gegen-/Mitsystem) [Prozent] mit Vorzeichen Drehsinn (ROT)",
                    feldname: "u_u2",
                    einheit: "",
                    unterkategorie: "Spannungsmessgrößen"
                });
            }
            if (regel.includes("Spannung 1 Stunde lang 0")) {
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u1",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u2",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u3",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u12",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u32",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
                messgroessen.push({
                    beschreibung: "Effektivwert",
                    feldname: "u31",
                    einheit: "V",
                    unterkategorie: "Spannungsmessgrößen"
                });
            }
            dispatch(
                onDropDownSelect({
                    field: "messgroesse",
                    value: messgroessen
                })
            );

            dispatch(
                updateField({
                    field: "startDatum",
                    value: nanosecondsTimeStampToDate(start)
                })
            );
            dispatch(
                updateField({
                    field: "endDatum",
                    value: nanosecondsTimeStampToDate(ende)
                })
            );
        }).catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        })
            .finally(() => history.push("/dashboardAnalyse"));
    }

    function setDialog(row: GridRowData, statusCode: messwertLogGeflaggtStatus, title: string, text: string) {
        const messwertTemp: messwertLogGeflaggt = {
            id: row.id,
            regel: row.regel,
            messstelle: row.messstelle,
            start: row.start,
            ende: row.ende,
            status: row.status,
            kommentar: row.kommentar,
        };
        dispatch(setAktuellerWert(messwertTemp));
        setDialogTitel(title);
        setDialogText(text);
        setDialogStatusCode(statusCode);
        setDialogKommentarInputValue(row.kommentar);
    }

    function interaktionSelectionBtn(row: GridRowData) {
        switch (row.status) {
            case "offen":
                return <div>
                    <Button color="secondary" variant="contained" style={{ marginRight: "10px" }} onClick={() => {
                        setVisibleDialog(true);
                        setDialog(row, messwertLogGeflaggtStatus.verwendet, "Verwenden", "Bitte bestätigen Sie Ihre Auswahl mit einem Kommentar");
                    }
                    }
                    >Verwenden</Button>
                    <Button className={classes.backgroundColorErrorDark} variant="contained" onClick={() => {
                        setVisibleDialog(true);
                        setDialog(row, messwertLogGeflaggtStatus.verworfen, "Verwerfen", "Bitte bestätigen Sie Ihre Auswahl mit einem Kommentar");
                    }
                    } >Verwerfen</Button>
                </div>;
            case "verwendet":
                return <Button color="default" variant="contained" onClick={() => {
                    setVisibleDialog(true);
                    setDialog(row, messwertLogGeflaggtStatus.offen, "Offen setzen", "Bitte bestätigen Sie Ihre Auswahl mit einem Kommentar");
                }
                }
                >Offen setzen</Button>;
            case "verworfen":
                return <Button color="default" variant="contained" onClick={() => {
                    setVisibleDialog(true);
                    setDialog(row, messwertLogGeflaggtStatus.offen, "Offen setzen", "Bitte bestätigen Sie Ihre Auswahl mit einem Kommentar");
                }
                }>Offen setzen</Button>;
            default: return "";
        }
    }

    const dataGridCols2: GridColDef[] = [
        {
            field: 'status', headerName: 'Status', flex: 0.6
        },
        {
            field: 'umspannwerk', headerName: 'Umspannwerk', flex: 0.6, filterable: false, valueGetter: (params: GridValueGetterParams) => {
                const messstellenName = String(params.getValue(params.id, "messstelle"));
                //eslint-disable-next-line security/detect-object-injection
                return messstellenDict[messstellenName] == undefined ? "Wird geladen..." : getMessstellenLookup(messstellenDict, messstellenName, "umspannwerk");
            }
        },
        {
            field: 'messstelle_schaltfeld', headerName: 'Messstelle', flex: 0.8, filterable: false, valueGetter: (params: GridValueGetterParams) => {
                const messstellenName = String(params.getValue(params.id, "messstelle"));
                const fieldNameAsString: string = getMessstellenLookup(messstellenDict, messstellenName, "schaltfeld") + " (" + messstellenName + ")";
                //eslint-disable-next-line security/detect-object-injection
                return messstellenDict[messstellenName] == undefined ? "Wird geladen..." : fieldNameAsString;
            }
        },
        {
            field: 'start', headerName: 'Start', flex: 0.5, filterable: false, valueFormatter: (params: GridValueFormatterParams) => {
                return (isUTC ? nanosecondsTimeStampToUTCStringWithoutSlice(params.row.start) : nanosecondsTimeStampToLocalStringWithoutSlice(params.row.start));
            }
        },
        {
            field: 'ende', headerName: 'Ende', flex: 0.5, filterable: false, valueFormatter: (params: GridValueFormatterParams) => {
                return (isUTC ? nanosecondsTimeStampToUTCStringWithoutSlice(params.row.ende) : nanosecondsTimeStampToLocalStringWithoutSlice(params.row.ende));
            }
        },
        { field: 'regel', headerName: 'Regel', flex: 0.8, filterable: false },
        {
            field: 'bearbeiten', headerName: 'Bearbeiten', flex: 0.8, filterable: false, renderCell: function status(params: GridCellParams) {
                return (<React.Fragment>{isAuthorized([rollen.admin, rollen.technik]) ? interaktionSelectionBtn(params.row) : ""}</React.Fragment>)
            }
        },
        {
            field: 'interaktion', headerName: 'Aktion', flex: 0.8, filterable: false, renderCell: function status(params: GridCellParams) {
                return (
                    <React.Fragment>
                        <IconButton
                            onClick={() => {
                                const messstellenName = String(params.getValue(params.id, "messstelle"));
                                linkToDetails(params.row.umspannwerk, params.row.start, params.row.ende, messstellenName, params.row.regel);
                            }}
                            color="secondary"
                            style={{ fontSize: "medium" }}
                        >Zur Detailansicht
                            <KeyboardArrowRight />
                        </IconButton>
                    </React.Fragment>
                )
            }
        }
    ]

    return (
        <Container maxWidth={false} className={classes.root}>
            <Grid item md={12} style={{ textAlign: "left", marginRight: "20px" }}>
                <div style={{ height: "50vh", width: '100%' }}>
                    <DataGrid
                        localeText={(deDE as LocalizationV4).props.MuiDataGrid.localeText}
                        disableColumnMenu={false}
                        disableColumnSelector={true}
                        columns={dataGridCols2}
                        rows={rows}
                        sortModel={sorting}
                        onSortModelChange={(model) => setSorting(model.sortModel)}
                        loading={listLoading}
                    />
                    <Dialog
                        open={visibleDialog}
                        onClose={() => setVisibleDialog(false)}
                    >
                        <DialogTitle>{dialogTitle}</DialogTitle>
                        <DialogContent>
                            <DialogContentText>{dialogText}</DialogContentText>
                            <TextField
                                required={true}
                                autoFocus
                                multiline
                                rows={3}
                                margin="dense"
                                id="begruendung"
                                label="Begründung"
                                variant="outlined"
                                fullWidth
                                value={dialogKommentar}
                                onChange={(event) => setDialogKommentarInputValue(event.target.value)}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Grid container md={12} spacing={2} style={{ marginBottom: "10px" }}>

                                <Grid item md={4} />
                                <Grid item md={8}>
                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            setVisibleDialog(false)
                                            setDialogKommentarInputValue("")
                                        }}
                                    >
                                        Abbrechen
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => {
                                            const messwertTemp: messwertLogGeflaggt = {
                                                id: aktuellerWert.id,
                                                regel: aktuellerWert.regel,
                                                messstelle: aktuellerWert.messstelle,
                                                start: aktuellerWert.start,
                                                ende: aktuellerWert.ende,
                                                status: dialogStatusCode,
                                                kommentar: dialogKommentar,
                                            };

                                            dispatch(setAktuellerWert(messwertTemp));
                                            dispatch(putMesswertGeflaggt(messwertTemp));
                                            setVisibleDialog(false);
                                        }}
                                        style={{ minWidth: "120px", minHeight: "36px", paddingLeft: "10px", marginLeft: "10px" }}
                                        disabled={
                                            dialogKommentar == "" || dialogKommentar == null
                                        }
                                    >
                                        {listLoading ? <CircularProgress size="20px" style={{ color: "black" }} /> : "Bestätigen"}
                                    </Button>
                                </Grid>
                            </Grid>
                        </DialogActions>
                    </Dialog>

                </div>
            </Grid>
        </Container>

    );
}